import type { Coin, Purse } from '../types/character'

/**
 * The SRD 5.2.1 Coin Values table, richest first — the order coins are shown
 * and the order a written amount reads in.
 */
export const COINS: { coin: Coin; label: string; abbr: string }[] = [
  { coin: 'pp', label: 'Platinum', abbr: 'pp' },
  { coin: 'gp', label: 'Gold', abbr: 'gp' },
  { coin: 'ep', label: 'Electrum', abbr: 'ep' },
  { coin: 'sp', label: 'Silver', abbr: 'sp' },
  { coin: 'cp', label: 'Copper', abbr: 'cp' },
]

/**
 * What may be written for each coin. Matching is case-insensitive and a
 * trailing "s" is forgiven, so "Platinums" and "coppers" resolve too.
 */
const ALIASES: Record<Coin, string[]> = {
  pp: ['p', 'pp', 'plat', 'platinum'],
  gp: ['g', 'gp', 'gc', 'gold'],
  ep: ['e', 'ep', 'elec', 'electrum'],
  sp: ['s', 'sp', 'sil', 'silver'],
  cp: ['c', 'cp', 'cop', 'copper'],
}

const BY_ALIAS = new Map<string, Coin>(
  Object.entries(ALIASES).flatMap(([coin, names]) => names.map((n) => [n, coin as Coin])),
)

/** Words that only glue an amount together and carry no value of their own. */
const FILLER = /^(?:and|plus|piece|pieces|pcs|coin|coins|[,;.&]|)$/

/** One "<number><unit>" term, e.g. "2g", "5 gp", "+ 3 Silver". */
const TERM = /([+-])?\s*(\d+)\s*([a-z]+)/g

export type CurrencyParse =
  | { ok: true; amounts: Purse }
  | { ok: false; error: string }

/**
 * Reads a written amount of money into per-coin numbers.
 *
 * Deliberately strict: anything it can't account for is an error rather than a
 * best guess, because the result is applied straight to the character's purse.
 * A bare number is rejected for the same reason — "5" could mean any coin.
 *
 *   "2g3s"                          → { gp: 2, sp: 3 }
 *   "1 Platinum 5gp 2Silver and 4c" → { pp: 1, gp: 5, sp: 2, cp: 4 }
 *   "-12 gold"                      → { gp: -12 }   (spending)
 */
export function parseCurrency(input: string): CurrencyParse {
  const text = input.toLowerCase()
  const amounts: Purse = {}
  let matched = false
  let cursor = 0
  const leftovers: string[] = []

  TERM.lastIndex = 0
  for (let m = TERM.exec(text); m; m = TERM.exec(text)) {
    leftovers.push(text.slice(cursor, m.index))
    cursor = m.index + m[0].length

    const coin = resolveCoin(m[3])
    if (!coin) return { ok: false, error: `“${m[3]}” isn’t a coin.` }
    const value = Number(m[2]) * (m[1] === '-' ? -1 : 1)
    amounts[coin] = (amounts[coin] ?? 0) + value
    matched = true
  }
  leftovers.push(text.slice(cursor))

  // Nothing may be silently ignored: whatever sits between the terms has to be
  // filler, or we didn't understand the input.
  for (const chunk of leftovers)
    for (const word of chunk.split(/[\s]+/)) {
      if (FILLER.test(word)) continue
      // A bare number is readable, just ambiguous — say which part is missing
      // rather than claiming we couldn't read it.
      if (/^[+-]?\d+$/.test(word))
        return { ok: false, error: `Write an amount with its coin, like “${word}gp” or “${word} silver”.` }
      return { ok: false, error: `Couldn’t read “${word.trim()}”.` }
    }

  if (!matched)
    return { ok: false, error: 'Write an amount with its coin, like “2g 3s” or “1 platinum”.' }
  return { ok: true, amounts }
}

/** The coin a written unit refers to, forgiving a plural "s". */
function resolveCoin(unit: string): Coin | undefined {
  return BY_ALIAS.get(unit) ?? (unit.length > 1 && unit.endsWith('s') ? BY_ALIAS.get(unit.slice(0, -1)) : undefined)
}

/**
 * Adds `delta` to a purse. Coins can't go negative — spending more than you
 * carry empties that coin rather than owing it, since nothing here makes
 * change between denominations.
 */
export function addCurrency(purse: Purse | undefined, delta: Purse): Purse {
  const next: Purse = { ...purse }
  for (const { coin } of COINS) {
    const total = (next[coin] ?? 0) + (delta[coin] ?? 0)
    if (total > 0) next[coin] = total
    else delete next[coin]
  }
  return next
}

/** A purse written back out, richest coin first ("1pp 5gp 4cp"). Empty → "0gp". */
export function formatCurrency(purse: Purse | undefined): string {
  const parts = COINS.filter(({ coin }) => (purse?.[coin] ?? 0) > 0).map(
    ({ coin }) => `${purse![coin]}${coin}`,
  )
  return parts.length ? parts.join(' ') : '0gp'
}
